import React, { useCallback, useEffect, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";

const ItemType = {
  TILE: "puzzle-tile",
};

const clampGridSize = (value, fallback) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(5, Math.max(2, Math.round(number)));
};

const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const shuffleTiles = (count) => {
  const items = Array.from({ length: count }, (_, idx) => idx);

  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }

  const isSolved = items.every((tile, idx) => tile === idx);
  if (isSolved && items.length > 1) {
    [items[0], items[1]] = [items[1], items[0]];
  }

  return items;
};

const getHorizontalEdgeSign = (row, column) => ((row + column) % 2 === 0 ? 1 : -1);
const getVerticalEdgeSign = (row, column) => ((row + column) % 2 === 0 ? -1 : 1);

const getTileEdges = (tileIndex, rows, columns) => {
  const row = Math.floor(tileIndex / columns);
  const column = tileIndex % columns;

  return {
    top: row === 0 ? 0 : -getHorizontalEdgeSign(row - 1, column),
    right: column === columns - 1 ? 0 : getVerticalEdgeSign(row, column),
    bottom: row === rows - 1 ? 0 : getHorizontalEdgeSign(row, column),
    left: column === 0 ? 0 : -getVerticalEdgeSign(row, column - 1),
  };
};

const createPuzzlePath = ({ top, right, bottom, left }) => {
  const knob = 12;
  const neckA = 36;
  const neckB = 64;

  const topY = -top * knob;
  const rightX = 100 + right * knob;
  const bottomY = 100 + bottom * knob;
  const leftX = -left * knob;

  return [
    "M 0 0",
    top
      ? `L ${neckA} 0 C ${neckA + 3} ${topY} ${neckB - 3} ${topY} ${neckB} 0`
      : "L 100 0",
    top ? "L 100 0" : "",
    right
      ? `L 100 ${neckA} C ${rightX} ${neckA + 3} ${rightX} ${neckB - 3} 100 ${neckB}`
      : "L 100 100",
    right ? "L 100 100" : "",
    bottom
      ? `L ${neckB} 100 C ${neckB - 3} ${bottomY} ${neckA + 3} ${bottomY} ${neckA} 100`
      : "L 0 100",
    bottom ? "L 0 100" : "",
    left
      ? `L 0 ${neckB} C ${leftX} ${neckB - 3} ${leftX} ${neckA + 3} 0 ${neckA}`
      : "L 0 0",
    left ? "L 0 0" : "",
    "Z",
  ].filter(Boolean).join(" ");
};

const PuzzleTile = ({ tileIndex, slotIndex, rows, columns, imgPath, isSolved, onSwap }) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemType.TILE,
      item: { slotIndex },
      canDrag: !isSolved,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [slotIndex, isSolved]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemType.TILE,
      canDrop: () => !isSolved,
      drop: (item) => onSwap(item.slotIndex, slotIndex),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [slotIndex, isSolved, onSwap]
  );

  const row = Math.floor(tileIndex / columns);
  const column = tileIndex % columns;
  const edges = getTileEdges(tileIndex, rows, columns);
  const path = createPuzzlePath(edges);
  const clipId = `puzzle-clip-${rows}-${columns}-${tileIndex}`;

  return (
    <button
      ref={(node) => drag(drop(node))}
      type="button"
      className={[
        "puzzle-tile",
        isDragging ? "is-dragging" : "",
        isOver ? "is-over" : "",
        isSolved ? "is-solved" : "",
      ].filter(Boolean).join(" ")}
      aria-label={`Частинка пазла ${tileIndex + 1}`}
    >
      <svg
        className="puzzle-tile-svg"
        viewBox="-14 -14 128 128"
        aria-hidden="true"
        focusable="false"
        preserveAspectRatio="none"
      >
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path d={path} />
          </clipPath>
        </defs>
        <image
          href={imgPath}
          x={-column * 100}
          y={-row * 100}
          width={columns * 100}
          height={rows * 100}
          preserveAspectRatio="none"
          clipPath={`url(#${clipId})`}
        />
        <path className="puzzle-tile-edge" d={path} />
        <path className="puzzle-tile-shine" d={path} />
      </svg>
    </button>
  );
};

const PuzzleQuestionContent = ({
  question,
  imgPath,
  rows,
  columns,
  showPreview,
  onSolved,
  showNextButton,
  onNext,
  showFinishButton,
  onFinish,
}) => {
  const safeRows = clampGridSize(rows, 3);
  const safeColumns = clampGridSize(columns, 3);
  const tileCount = safeRows * safeColumns;
  const solvedRef = useRef(false);

  const [tiles, setTiles] = useState(() => shuffleTiles(tileCount));

  useEffect(() => {
    solvedRef.current = false;
    setTiles(shuffleTiles(tileCount));
  }, [tileCount, imgPath]);

  const solved = tiles.every((tile, idx) => tile === idx);
  const placedCount = tiles.reduce((sum, tile, idx) => sum + (tile === idx ? 1 : 0), 0);

  useEffect(() => {
    if (!solved || solvedRef.current) return;
    solvedRef.current = true;
    onSolved?.();
  }, [solved, onSolved]);

  const swapTiles = useCallback((fromSlot, toSlot) => {
    if (fromSlot === toSlot) return;

    setTiles((prev) => {
      const next = [...prev];
      [next[fromSlot], next[toSlot]] = [next[toSlot], next[fromSlot]];
      return next;
    });
  }, []);

  const resetPuzzle = () => {
    solvedRef.current = false;
    setTiles(shuffleTiles(tileCount));
  };

  if (!imgPath) {
    return (
      <div className="puzzle-empty">
        Для пазла потрібно додати зображення в адмінці.
      </div>
    );
  }

  return (
    <div className="puzzle-question">
      {String(question || "").trim() && (
        <div className="test-question" dangerouslySetInnerHTML={{ __html: question }} />
      )}

      <div className="puzzle-layout">
        <div className="puzzle-stage">
          <div
            className={`puzzle-board${solved ? " is-solved" : ""}`}
            style={{
              "--puzzle-rows": safeRows,
              "--puzzle-columns": safeColumns,
            }}
          >
            {tiles.map((tileIndex, slotIndex) => (
              <PuzzleTile
                key={slotIndex}
                tileIndex={tileIndex}
                slotIndex={slotIndex}
                rows={safeRows}
                columns={safeColumns}
                imgPath={imgPath}
                isSolved={solved}
                onSwap={swapTiles}
              />
            ))}
          </div>
        </div>

        {showPreview !== false && (
          <aside className="puzzle-preview">
            <span>Зразок</span>
            <img src={imgPath} alt="Зразок пазла" />
          </aside>
        )}
      </div>

      <div className="puzzle-actions">
        <button type="button" className="puzzle-reset" onClick={resetPuzzle}>
          Перемішати
        </button>
        <div className={`puzzle-progress${solved ? " is-solved" : ""}`}>
          {solved ? "Пазл складено" : `На місці: ${placedCount}/${tileCount}`}
        </div>
      </div>

      {solved && showNextButton && (
        <button className="btn nextAnswer" onClick={onNext} style={{ display: "inline-block" }}>
          Наступне завдання
        </button>
      )}

      {solved && showFinishButton && (
        <button className="btn nextAnswer" onClick={onFinish} style={{ display: "inline-block" }}>
          Завершити
        </button>
      )}
    </div>
  );
};

const PuzzleQuestion = (props) => (
  <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
    <PuzzleQuestionContent {...props} />
  </DndProvider>
);

export default PuzzleQuestion;
